/**
 * SearchableSelect.js - Lightweight type-to-filter dropdown for captain selection
 * CSS prefix: ss- (to avoid CRA global CSS collisions)
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import './SearchableSelect.css';

function SearchableSelect({ options, value, onChange, placeholder = 'Select...', createLabel, onCreateNew }) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const selected = options.find(o => o.value === value);

  const filtered = options.filter(o => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return o.label.toLowerCase().includes(q);
  });

  const allItems = onCreateNew
    ? [...filtered, { value: '__create__', label: createLabel || '+ Create New' }]
    : filtered;

  const handleSelect = useCallback((val) => {
    if (val === '__create__' && onCreateNew) {
      onCreateNew();
    } else {
      onChange(val);
    }
    setOpen(false);
    setFilter('');
    setHighlightIndex(-1);
  }, [onChange, onCreateNew]);

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex(i => Math.min(i + 1, allItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightIndex >= 0 && highlightIndex < allItems.length) {
        handleSelect(allItems[highlightIndex].value);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setFilter('');
      setHighlightIndex(-1);
    }
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setFilter('');
        setHighlightIndex(-1);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }
  }, [open]);

  // Focus input when opened
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightIndex >= 0 && containerRef.current) {
      const item = containerRef.current.querySelector(`[data-index="${highlightIndex}"]`);
      if (item) item.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIndex]);

  return (
    <div className="ss-container" ref={containerRef}>
      <button
        type="button"
        className="ss-trigger"
        onClick={() => setOpen(!open)}
        onKeyDown={handleKeyDown}
      >
        <span className={selected ? 'ss-trigger-text' : 'ss-trigger-placeholder'}>
          {selected ? selected.label : placeholder}
        </span>
        <span className="ss-arrow">{open ? '\u25B2' : '\u25BC'}</span>
      </button>

      {open && (
        <div className="ss-dropdown">
          <input
            ref={inputRef}
            type="text"
            className="ss-filter-input"
            placeholder="Type to filter..."
            value={filter}
            onChange={e => { setFilter(e.target.value); setHighlightIndex(-1); }}
            onKeyDown={handleKeyDown}
          />
          <div className="ss-options">
            {/* Always show a "No Captain" / clear option */}
            <div
              className={`ss-option${!value ? ' ss-option-selected' : ''}${highlightIndex === -1 ? '' : ''}`}
              onClick={() => handleSelect('')}
            >
              {placeholder}
            </div>
            {allItems.length === 0 ? (
              <div className="ss-no-results">No matches found</div>
            ) : (
              allItems.map((opt, i) => (
                <div
                  key={opt.value}
                  data-index={i}
                  className={`ss-option${opt.value === value ? ' ss-option-selected' : ''}${i === highlightIndex ? ' ss-option-highlight' : ''}${opt.value === '__create__' ? ' ss-option-create' : ''}`}
                  onClick={() => handleSelect(opt.value)}
                  onMouseEnter={() => setHighlightIndex(i)}
                >
                  {opt.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchableSelect;
